// components/admin/ChannelForm.tsx
// Uses state and effects

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../components/ui/Input";
import TextArea from "../../components/ui/Textarea";
import Button from "../../components/ui/Button";
import ErrorMessage from "../../components/ui/ErrorMessage";
import {
  Channel,
  ChannelCreatePayload,
  ChannelUpdatePayload,
  ChannelPlatform,
} from "../../types";
import { PLATFORMS, FEATURE_FLAGS } from "../../lib/constants";
import {
  isValidDiscordGuildId,
  isValidDiscordRoleId,
  isValidDiscordChannelId,
  isValidSlackChannelId,
  getChannelPlatform,
  getErrorMessage,
} from "../../lib/utils";
import {
  getDiscordBotGuilds,
  getDiscordGuildRoles,
  getDiscordGuildChannels,
  getSlackWorkspaceChannels,
  createChannelOnboardingSession,
  DiscordGuild,
  DiscordRole,
  SlackChannel,
  ChannelOnboardingSession,
} from "../../lib/apiClient";

interface ChannelFormProps {
  initialData?: Channel | null;
  onSubmit: (
    data: ChannelCreatePayload | ChannelUpdatePayload
  ) => Promise<void>;
  isLoading: boolean;
  formError: string | null;
  isEditMode?: boolean;
  hasActiveSubscriptions?: boolean; // Prevent platform change if true
  integrationFlags?: {
    telegramEnabled?: boolean;
    discordEnabled?: boolean;
    slackEnabled?: boolean;
  };
}

const ChannelForm: React.FC<ChannelFormProps> = ({
  initialData,
  onSubmit,
  isLoading,
  formError,
  isEditMode = false,
  hasActiveSubscriptions = false,
  integrationFlags,
}) => {
  const navigate = useNavigate();
  const tenantFlags = {
    telegramEnabled: integrationFlags?.telegramEnabled !== false,
    discordEnabled: integrationFlags?.discordEnabled !== false,
    slackEnabled: integrationFlags?.slackEnabled !== false,
  };

  const platformAvailability = {
    [PLATFORMS.TELEGRAM]: tenantFlags.telegramEnabled,
    [PLATFORMS.DISCORD]:
      FEATURE_FLAGS.DISCORD_SUPPORT_ENABLED && tenantFlags.discordEnabled,
    [PLATFORMS.SLACK]: FEATURE_FLAGS.SLACK_SUPPORT_ENABLED && tenantFlags.slackEnabled,
  } as const;

  const enabledPlatforms = Object.entries(platformAvailability)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key as ChannelPlatform);
  const discordAvailable = platformAvailability[PLATFORMS.DISCORD];
  const slackAvailable = platformAvailability[PLATFORMS.SLACK];

  // --- Platform State ---
  const [platform, setPlatform] = useState<ChannelPlatform>(
    initialData ? getChannelPlatform(initialData) : PLATFORMS.TELEGRAM as ChannelPlatform
  );

  // --- Form Field State ---
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");

  // Telegram-specific fields
  const [telegramChatId, setTelegramChatId] = useState(
    initialData?.telegram_chat_id || ""
  );

  // Discord-specific fields
  const [discordGuildId, setDiscordGuildId] = useState(
    initialData?.discord_guild_id || ""
  );
  const [discordRoleId, setDiscordRoleId] = useState(
    initialData?.discord_role_id || ""
  );
  const [discordChannelId, setDiscordChannelId] = useState(
    initialData?.discord_channel_id || ""
  );

  // Slack-specific fields
  const [slackTeamId, setSlackTeamId] = useState(initialData?.slack_team_id || "");
  const [slackChannelId, setSlackChannelId] = useState(initialData?.slack_channel_id || "");
  const [slackWorkspaceUrl, setSlackWorkspaceUrl] = useState(
    initialData?.slack_workspace_url || ""
  );

  // Discord dropdown data
  const [discordGuilds, setDiscordGuilds] = useState<DiscordGuild[]>([]);
  const [discordRoles, setDiscordRoles] = useState<DiscordRole[]>([]);
  const [discordChannels, setDiscordChannels] = useState<Array<{ id: string; name: string; type: number }>>([]);
  const [isLoadingGuilds, setIsLoadingGuilds] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);

  // Slack dropdown data
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [isLoadingSlackChannels, setIsLoadingSlackChannels] = useState(false);
  const [isSlackSimulated, setIsSlackSimulated] = useState(false);

  // Common fields
  const [reminderTemplateOverrideId] = useState<string | null>(
    initialData?.reminder_template_override_id || null
  );
  const [reminderDaysOverride, setReminderDaysOverride] = useState<string>(
    initialData?.reminder_days_override?.toString() ?? ""
  );
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [ispost, setIspost] = useState(initialData?.is_post ?? true);
  const [couponCode, setCouponCode] = useState(initialData?.couponCode || "");
  const [couponDiscount, setCouponDiscount] = useState<string>(
    initialData?.couponDiscount?.toString() ?? ""
  );

  // --- State for Local Validation ---
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Onboarding wizard session (create flow)
  const [onboardingSession, setOnboardingSession] =
    useState<ChannelOnboardingSession | null>(null);
  const [isLoadingOnboardingSession, setIsLoadingOnboardingSession] =
    useState(false);
  const [onboardingSessionError, setOnboardingSessionError] =
    useState<string | null>(null);

  const loadOnboardingSession = useCallback(async () => {
    if (isEditMode) return;
    setIsLoadingOnboardingSession(true);
    setOnboardingSessionError(null);
    try {
      const session = await createChannelOnboardingSession(platform);
      setOnboardingSession(session);
    } catch (error) {
      setOnboardingSession(null);
      setOnboardingSessionError(getErrorMessage(error));
    } finally {
      setIsLoadingOnboardingSession(false);
    }
  }, [isEditMode, platform]);

  // --- Load Discord guilds when Discord platform is selected ---
  useEffect(() => {
    if (platform === PLATFORMS.DISCORD && discordAvailable) {
      loadDiscordGuilds();
    }
    if (platform === PLATFORMS.SLACK && slackAvailable) {
      loadSlackChannels();
    }
  }, [platform, discordAvailable, slackAvailable]);

  useEffect(() => {
    if (enabledPlatforms.length === 0) return;
    if (!enabledPlatforms.includes(platform)) {
      setPlatform(enabledPlatforms[0]);
    }
  }, [enabledPlatforms, platform]);

  useEffect(() => {
    loadOnboardingSession();
  }, [loadOnboardingSession]);

  // --- Load Discord roles when guild is selected ---
  useEffect(() => {
    if (discordGuildId && platform === PLATFORMS.DISCORD) {
      loadDiscordRoles(discordGuildId);
      loadDiscordChannels(discordGuildId);
    } else {
      setDiscordRoles([]);
      setDiscordChannels([]);
    }
  }, [discordGuildId, platform]);

  const loadDiscordGuilds = async () => {
    setIsLoadingGuilds(true);
    try {
      const { guilds } = await getDiscordBotGuilds();
      setDiscordGuilds(guilds);
    } catch (error) {
      console.error("Failed to load Discord guilds:", error);
    } finally {
      setIsLoadingGuilds(false);
    }
  };

  const loadDiscordRoles = async (guildId: string) => {
    setIsLoadingRoles(true);
    try {
      const { roles } = await getDiscordGuildRoles(guildId);
      setDiscordRoles(roles);
    } catch (error) {
      console.error("Failed to load Discord roles:", error);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const loadDiscordChannels = async (guildId: string) => {
    setIsLoadingChannels(true);
    try {
      const { channels } = await getDiscordGuildChannels(guildId);
      // Filter to only text channels (type 0)
      setDiscordChannels(channels.filter(c => c.type === 0));
    } catch (error) {
      console.error("Failed to load Discord channels:", error);
    } finally {
      setIsLoadingChannels(false);
    }
  };

  const loadSlackChannels = async () => {
    setIsLoadingSlackChannels(true);
    try {
      const { channels, simulated } = await getSlackWorkspaceChannels();
      setIsSlackSimulated(!!simulated);
      setSlackChannels(channels);
    } catch (error) {
      console.error("Failed to load Slack channels:", error);
      setIsSlackSimulated(false);
    } finally {
      setIsLoadingSlackChannels(false);
    }
  };

  const normalizeSlackWorkspaceUrl = (raw: string) => {
    const value = (raw || "").trim();
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
  };

  // --- Validation Logic ---
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (enabledPlatforms.length === 0) {
      errors.platform = "No integrations are enabled for this account.";
      setValidationErrors(errors);
      return false;
    }

    if (!platformAvailability[platform]) {
      errors.platform = `${platform} integration is disabled for this account.`;
      setValidationErrors(errors);
      return false;
    }

    if (!name.trim() && !isTelegramCreateFlow) errors.name = "Channel name is required.";

    // Platform-specific validation
    if (platform === PLATFORMS.TELEGRAM) {
      // telegram_chat_id is auto-populated by the bot-join webhook — no manual entry required.
      // In edit mode, if a value is pre-filled it must still be valid.
      if (isEditMode && telegramChatId.trim() && !/^([@a-zA-Z0-9_]{5,32}|-100\d{9,13})$/.test(telegramChatId.trim())) {
        errors.telegramChatId = "Invalid value. Use @channelusername (public) or numeric Chat ID starting with -100 (private).";
      }
    } else if (platform === PLATFORMS.DISCORD) {
      if (!discordGuildId.trim()) {
        errors.discordGuildId = "Discord Server (Guild) is required.";
      } else if (!isValidDiscordGuildId(discordGuildId.trim())) {
        errors.discordGuildId = "Invalid Discord Guild ID format.";
      }

      if (!discordRoleId.trim()) {
        errors.discordRoleId = "Discord Role is required.";
      } else if (!isValidDiscordRoleId(discordRoleId.trim())) {
        errors.discordRoleId = "Invalid Discord Role ID format.";
      }

      if (discordChannelId.trim() && !isValidDiscordChannelId(discordChannelId.trim())) {
        errors.discordChannelId = "Invalid Discord Channel ID format.";
      }
    } else if (platform === PLATFORMS.SLACK) {
      if (isSlackSimulated) {
        errors.slackChannelId =
          "Cannot add Slack channels because SLACK_BOT_TOKEN is not configured on the backend.";
      }

      if (!slackChannelId.trim()) {
        errors.slackChannelId = "Slack Channel is required.";
      } else if (!isValidSlackChannelId(slackChannelId.trim())) {
        errors.slackChannelId = "Invalid Slack Channel ID format.";
      }

      const normalizedSlackWorkspaceUrl = normalizeSlackWorkspaceUrl(slackWorkspaceUrl);
      if (!isEditMode && !normalizedSlackWorkspaceUrl) {
        errors.slackWorkspaceUrl =
          "Slack Workspace Join URL is required for creating a Slack channel.";
      }

      if (normalizedSlackWorkspaceUrl) {
        try {
          const parsedUrl = new URL(normalizedSlackWorkspaceUrl);

          const isSlackInviteUrl =
            /^join\.slack\.com$/i.test(parsedUrl.hostname) &&
            /^\/t\/[^/]+\/shared_invite\//i.test(parsedUrl.pathname);

          if (!isSlackInviteUrl) {
            errors.slackWorkspaceUrl =
              "Use only this format: https://join.slack.com/t/<workspace>/shared_invite/...";
          }
        } catch {
          errors.slackWorkspaceUrl = "Invalid Slack Workspace Join URL.";
        }
      }
    }

    // Common validation
    if (
      reminderDaysOverride !== "" &&
      (isNaN(Number(reminderDaysOverride)) || Number(reminderDaysOverride) < 0)
    ) {
      errors.reminderDaysOverride =
        "Reminder days must be a non-negative number.";
    }

    if (couponCode.trim() !== "" && couponCode.trim().length < 6) {
      errors.couponCode = "Coupon code must be at least 6 characters.";
    }

    if (
      couponDiscount.trim() !== "" &&
      (isNaN(Number(couponDiscount)) ||
        Number(couponDiscount) < 0 ||
        Number(couponDiscount) > 100)
    ) {
      errors.couponDiscount = "Discount percentage must be between 0 and 100.";
    } else if (couponDiscount.trim() === "" && couponCode.trim() !== "") {
      errors.couponDiscount =
        "Discount is required if coupon code is provided.";
    } else if (
      couponDiscount.trim() !== "" &&
      couponDiscount.trim() !== "0" &&
      couponCode.trim() === ""
    ) {
      errors.couponCode = "Coupon code is required if discount is provided.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // --- Form Submission Handler ---
  const handleSubmit = (e: React.FormEvent) => {
    console.log("Submitting channel form...");
    e.preventDefault();
    setValidationErrors({});

    if (!validateForm()) {
      console.log("Channel form validation failed.");
      return;
    }

    const daysOverrideValue =
      reminderDaysOverride === "" ? null : Number(reminderDaysOverride);
    const couponDiscountValue =
      couponDiscount === "" ? null : Number(couponDiscount);

    const baseFormData = {
      name: name.trim(),
      description: description.trim() || null,
      reminder_template_override_id: reminderTemplateOverrideId || null,
      reminder_days_override: daysOverrideValue,
      is_active: isActive,
      is_post: ispost,
      couponCode: couponCode.trim() || null,
      couponDiscount: couponDiscountValue,
      platform,
    };

    let formData: ChannelCreatePayload | ChannelUpdatePayload;

    if (platform === PLATFORMS.TELEGRAM) {
      formData = {
        ...baseFormData,
        telegram_chat_id: telegramChatId.trim() || null,
        discord_guild_id: null,
        discord_role_id: null,
        discord_channel_id: null,
        slack_team_id: null,
        slack_channel_id: null,
        slack_workspace_url: null,
      };
    } else if (platform === PLATFORMS.DISCORD) {
      formData = {
        ...baseFormData,
        telegram_chat_id: null,
        discord_guild_id: discordGuildId.trim(),
        discord_role_id: discordRoleId.trim(),
        discord_channel_id: discordChannelId.trim() || null,
        slack_team_id: null,
        slack_channel_id: null,
        slack_workspace_url: null,
      };
    } else {
      formData = {
        ...baseFormData,
        telegram_chat_id: null,
        discord_guild_id: null,
        discord_role_id: null,
        discord_channel_id: null,
        slack_team_id: slackTeamId.trim() || null,
        slack_channel_id: slackChannelId.trim(),
        slack_workspace_url:
          normalizeSlackWorkspaceUrl(slackWorkspaceUrl) || null,
      };
    }

    console.log(formData);
    onSubmit(formData);
  };

  const isTelegram = platform === PLATFORMS.TELEGRAM;
  const isDiscord = platform === PLATFORMS.DISCORD;
  const isSlack = platform === PLATFORMS.SLACK;
  const canChangePlatform = !isEditMode || !hasActiveSubscriptions;
  // For Telegram create — no form needed, channel auto-created by webhook
  const isTelegramCreateFlow = isTelegram && !isEditMode;
  const channelInfoStepNumber = isEditMode ? "2" : "3";
  const reminderStepNumber = isEditMode ? "3" : "4";
  const couponStepNumber = isEditMode ? "4" : "5";
  const statusStepNumber = isEditMode ? "5" : "6";
  const platformLabelMap: Record<ChannelPlatform, string> = {
    telegram: "Telegram",
    discord: "Discord",
    slack: "Slack",
  };

  // --- Render Form ---
  return (
    <form
      onSubmit={handleSubmit}
      className="bg-gradient-to-br from-dark-secondary via-dark-secondary to-dark-tertiary/20 p-8 rounded-xl shadow-2xl border border-dark-tertiary/60 backdrop-blur-sm"
    >
      {formError && (
        <ErrorMessage
          message={formError}
          className="mb-4"
          title="Submission Error"
        />
      )}

      {validationErrors.platform && (
        <ErrorMessage
          message={validationErrors.platform}
          className="mb-4"
          title="Integration Access"
        />
      )}

      {/* 1. Platform Selection */}
      <div className="space-y-6 mb-8">
        <div className="pb-4 border-b border-dark-tertiary/30">
          <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
            <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
              <span className="text-golden-accent font-bold text-sm">1</span>
            </div>
            Platform
          </h2>
          <p className="text-text-secondary text-sm ml-11">
            Select the platform for this channel
            {!canChangePlatform && (
              <span className="text-functional-warning ml-2">
                (Cannot change platform with active subscriptions)
              </span>
            )}
          </p>
        </div>

        <div className="ml-11 max-w-md">
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Desired Platform *
          </label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as ChannelPlatform)}
            disabled={!canChangePlatform || isLoading || enabledPlatforms.length === 0}
            className="w-full px-4 py-3 bg-dark-tertiary border border-dark-border rounded-lg text-text-primary focus:ring-2 focus:ring-golden-accent focus:border-transparent"
          >
            {enabledPlatforms.map((value) => (
              <option key={value} value={value}>
                {platformLabelMap[value]}
              </option>
            ))}
          </select>
          <p className="text-text-secondary text-xs mt-1">
            Choosing a platform refreshes the onboarding session QR and bot authorization link.
          </p>
        </div>
      </div>

      {/* 2. QR-First Onboarding Wizard (Create flow only) */}
      {!isEditMode && (
        <div className="space-y-6 mb-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">2</span>
              </div>
              Bot Authorization Wizard
            </h2>
            <p className="text-text-secondary text-sm ml-11">
              {isTelegramCreateFlow
                ? "Scan or open the link below to add the bot to your Telegram channel/group."
                : "Scan QR or open link to authorize bot/app, then complete channel details."}
            </p>
          </div>

          <div className="ml-11">
            {isLoadingOnboardingSession && (
              <div className="rounded-lg border border-dark-border bg-dark-primary/40 p-4 text-text-secondary text-sm">
                Generating secure onboarding session...
              </div>
            )}

            {onboardingSessionError && (
              <ErrorMessage
                title="Onboarding Session"
                message={onboardingSessionError}
                className="mb-4"
              />
            )}

            {onboardingSession && isTelegramCreateFlow && !onboardingSession.deepLinkUrl ? (
              // Bot not configured — redirect to settings
              <div className="rounded-lg border border-functional-warning/40 bg-functional-warning/10 p-6">
                <p className="text-sm font-semibold text-functional-warning mb-1">Telegram bot not configured</p>
                <p className="text-sm text-text-secondary mb-4">
                  Bot Token and Bot Username must be saved in Settings before you can add a Telegram channel.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/admin/settings")}
                  className="px-4 py-2 rounded-lg bg-golden-accent text-text-on-accent text-sm font-medium hover:bg-golden-accent-hover transition-colors"
                >
                  Go to Settings
                </button>
              </div>
            ) : onboardingSession ? (
              <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
                <div className="bg-dark-primary/50 border border-dark-border rounded-lg p-4">
                  <p className="text-sm text-text-secondary mb-3">
                    Scan QR to open {platformLabelMap[platform]} bot authorization
                  </p>
                  {onboardingSession.qrCodeUrl ? (
                    <img
                      src={onboardingSession.qrCodeUrl}
                      alt={`${platformLabelMap[platform]} onboarding QR`}
                      className="w-full max-w-[280px] h-auto rounded border border-dark-border mx-auto"
                    />
                  ) : (
                    <div className="rounded border border-functional-warning/30 bg-functional-warning/10 p-3 text-xs text-functional-warning">
                      QR unavailable. Configure bot/app authorization URL first.
                    </div>
                  )}

                  {onboardingSession.deepLinkUrl && (
                    <a
                      href={onboardingSession.deepLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-golden-accent/50 px-3 py-2 text-sm font-medium text-golden-accent hover:bg-golden-accent/10"
                    >
                      {onboardingSession.ctaLabel || `Open ${platformLabelMap[platform]} Link`}
                    </a>
                  )}
                </div>

                <div className="bg-dark-primary/30 border border-dark-border rounded-lg p-4">
                  <h3 className="text-base font-semibold text-text-primary mb-2">
                    {platformLabelMap[platform]} Onboarding Session
                  </h3>
                  <p className="text-xs text-text-secondary mb-4">
                    Session ID: {onboardingSession.sessionId}
                  </p>

                  {isTelegramCreateFlow ? (
                    <>
                      <ol className="list-decimal list-inside space-y-3 text-sm text-text-secondary mb-4">
                        <li>Open the bot link above in Telegram</li>
                        <li>Add the bot to your channel or group</li>
                        <li>Promote the bot to <strong className="text-text-primary">Admin</strong></li>
                      </ol>
                      <div className="rounded-lg border border-functional-success/30 bg-functional-success/10 p-4">
                        <p className="text-sm font-medium text-functional-success mb-1">That's it!</p>
                        <p className="text-sm text-text-secondary">
                          Once the bot is added as admin, the channel will appear automatically in your channel list.
                          The channel name and ID are detected from Telegram — no manual setup needed.
                        </p>
                      </div>
                    </>
                  ) : (
                    <ol className="list-decimal list-inside space-y-2 text-sm text-text-secondary mb-4">
                      {onboardingSession.steps.map((step, index) => (
                        <li key={`${onboardingSession.sessionId}-${index}`}>{step}</li>
                      ))}
                    </ol>
                  )}

                  <div className="rounded border border-dark-border bg-dark-tertiary/40 p-3 mb-4">
                    <p className="text-xs text-text-secondary mb-1">Bot/App link</p>
                    {onboardingSession.deepLinkUrl ? (
                      <a
                        href={onboardingSession.deepLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-functional-info break-all hover:underline"
                      >
                        {onboardingSession.linkDisplay || onboardingSession.deepLinkUrl}
                      </a>
                    ) : (
                      <p className="text-xs text-functional-warning">
                        Missing authorization link configuration.
                      </p>
                    )}
                  </div>

                  {onboardingSession.configHints?.length > 0 && (
                    <div className="rounded border border-functional-warning/30 bg-functional-warning/10 p-3 mb-4">
                      <p className="text-xs font-medium text-functional-warning mb-1">
                        Configuration needed:
                      </p>
                      <ul className="list-disc list-inside text-xs text-functional-warning/90 space-y-1">
                        {onboardingSession.configHints.map((hint, index) => (
                          <li key={`hint-${index}`}>{hint}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={loadOnboardingSession}
                    disabled={isLoadingOnboardingSession}
                  >
                    Regenerate Session QR
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* For Telegram create: no form needed — channel auto-created by webhook */}
      {isTelegramCreateFlow && (
        <div className="rounded-lg border border-dark-border/40 bg-dark-primary/30 p-5 text-center">
          <p className="text-text-secondary text-sm">
            Waiting for bot to be added to a channel...
          </p>
          <p className="text-text-secondary text-xs mt-2">
            Once added, go to <strong className="text-text-primary">Channels</strong> to see it.
          </p>
        </div>
      )}

      {/* Channel details form — only for Discord/Slack create or any edit */}
      {!isTelegramCreateFlow && (
        <>
      {/* 2. Basic Channel Info */}
      <div className="space-y-6">
        <div className="pb-4 border-b border-dark-tertiary/30">
          <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
            <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
              <span className="text-golden-accent font-bold text-sm">
                {channelInfoStepNumber}
              </span>
            </div>
            Channel Information
          </h2>
          <p className="text-text-secondary text-sm ml-11">Basic details about the channel</p>
        </div>

        <div className="ml-11 space-y-6">
          <Input
            label="Channel Name"
            id="channel-name"
            name="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setValidationErrors({ ...validationErrors, name: undefined });
            }}
            required
            disabled={isLoading}
            error={validationErrors.name}
            maxLength={100}
          />

          {/* Telegram-specific fields */}
          {isTelegram && (
            <div>
              {isEditMode && initialData?.telegram_chat_id ? (
                <div className="mb-4">
                  <p className="text-sm font-medium text-text-secondary mb-1">Telegram Chat ID</p>
                  <code className="text-sm text-text-primary bg-dark-primary/60 border border-dark-border rounded px-3 py-2 block">
                    {initialData.telegram_chat_id}
                  </code>
                  <p className="text-xs text-text-secondary mt-1">Set automatically by the bot join event. Cannot be changed.</p>
                </div>
              ) : (
                <div className="rounded-lg border border-golden-accent/30 bg-golden-accent/5 p-4">
                  <p className="text-sm font-medium text-text-primary mb-1">Chat ID is set automatically</p>
                  <p className="text-sm text-text-secondary">
                    Add the bot to your Telegram channel or group as an admin using the QR code above.
                    The Chat ID will be detected and saved automatically — no manual entry needed.
                  </p>
                </div>
              )}
              {validationErrors.telegramChatId && (
                <p className="text-functional-danger text-xs mt-1">{validationErrors.telegramChatId}</p>
              )}
            </div>
          )}

          {/* Discord-specific fields */}
          {isDiscord && (
            <>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Discord Server (Guild) *
                </label>
                <select
                  value={discordGuildId}
                  onChange={(e) => {
                    setDiscordGuildId(e.target.value);
                    setDiscordRoleId("");
                    setDiscordChannelId("");
                    setValidationErrors({
                      ...validationErrors,
                      discordGuildId: undefined,
                    });
                  }}
                  disabled={isLoading || isLoadingGuilds || (isEditMode && !!initialData?.discord_guild_id)}
                  className="w-full px-4 py-3 bg-dark-tertiary border border-dark-border rounded-lg text-text-primary focus:ring-2 focus:ring-golden-accent focus:border-transparent"
                >
                  <option value="">
                    {isLoadingGuilds ? "Loading servers..." : "Select a server"}
                  </option>
                  {discordGuilds.map((guild) => (
                    <option key={guild.id} value={guild.id}>
                      {guild.name}
                    </option>
                  ))}
                </select>
                {validationErrors.discordGuildId && (
                  <p className="text-functional-danger text-xs mt-1">
                    {validationErrors.discordGuildId}
                  </p>
                )}
                <p className="text-text-secondary text-xs mt-1">
                  The Discord server where the bot is installed
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Discord Role *
                </label>
                <select
                  value={discordRoleId}
                  onChange={(e) => {
                    setDiscordRoleId(e.target.value);
                    setValidationErrors({
                      ...validationErrors,
                      discordRoleId: undefined,
                    });
                  }}
                  disabled={isLoading || isLoadingRoles || !discordGuildId}
                  className="w-full px-4 py-3 bg-dark-tertiary border border-dark-border rounded-lg text-text-primary focus:ring-2 focus:ring-golden-accent focus:border-transparent"
                >
                  <option value="">
                    {isLoadingRoles
                      ? "Loading roles..."
                      : !discordGuildId
                      ? "Select a server first"
                      : "Select a role"}
                  </option>
                  {discordRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {validationErrors.discordRoleId && (
                  <p className="text-functional-danger text-xs mt-1">
                    {validationErrors.discordRoleId}
                  </p>
                )}
                <p className="text-text-secondary text-xs mt-1">
                  The role that will be assigned to subscribers
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Discord Channel (Optional)
                </label>
                <select
                  value={discordChannelId}
                  onChange={(e) => {
                    setDiscordChannelId(e.target.value);
                    setValidationErrors({
                      ...validationErrors,
                      discordChannelId: undefined,
                    });
                  }}
                  disabled={isLoading || isLoadingChannels || !discordGuildId}
                  className="w-full px-4 py-3 bg-dark-tertiary border border-dark-border rounded-lg text-text-primary focus:ring-2 focus:ring-golden-accent focus:border-transparent"
                >
                  <option value="">
                    {isLoadingChannels
                      ? "Loading channels..."
                      : !discordGuildId
                      ? "Select a server first"
                      : "Select a channel (optional)"}
                  </option>
                  {discordChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      #{channel.name}
                    </option>
                  ))}
                </select>
                {validationErrors.discordChannelId && (
                  <p className="text-functional-danger text-xs mt-1">
                    {validationErrors.discordChannelId}
                  </p>
                )}
                <p className="text-text-secondary text-xs mt-1">
                  Used for generating invite links (optional)
                </p>
              </div>
            </>
          )}

          {/* Slack-specific fields */}
          {isSlack && (
            <>
              {isSlackSimulated && (
                <div className="rounded-lg border border-functional-warning/40 bg-functional-warning/10 p-3">
                  <p className="text-sm text-functional-warning font-medium">
                    Slack token not configured. You cannot add Slack channels right now.
                  </p>
                  <p className="text-xs text-text-secondary mt-1">
                    Please set SLACK_BOT_TOKEN in backend environment and restart the server.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Slack Channel *
                </label>
                <select
                  value={slackChannelId}
                  onChange={(e) => {
                    setSlackChannelId(e.target.value);
                    setValidationErrors({
                      ...validationErrors,
                      slackChannelId: undefined,
                    });
                  }}
                  disabled={isLoading || isLoadingSlackChannels || isSlackSimulated}
                  className="w-full px-4 py-3 bg-dark-tertiary border border-dark-border rounded-lg text-text-primary focus:ring-2 focus:ring-golden-accent focus:border-transparent"
                >
                  <option value="">
                    {isLoadingSlackChannels ? "Loading channels..." : "Select a Slack channel"}
                  </option>
                  {slackChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.is_private ? "[Private] " : "[Public] "}
                      #{channel.name}
                    </option>
                  ))}
                </select>
                {validationErrors.slackChannelId && (
                  <p className="text-functional-danger text-xs mt-1">
                    {validationErrors.slackChannelId}
                  </p>
                )}
                <p className="text-text-secondary text-xs mt-1">
                  Select the Slack channel where subscribers will be invited
                </p>
              </div>

              <Input
                label="Slack Team ID (Optional)"
                id="slack-team-id"
                name="slack_team_id"
                value={slackTeamId}
                onChange={(e) => setSlackTeamId(e.target.value)}
                placeholder="T0123456789"
                disabled={isLoading}
              />

              <Input
                label={`Slack Workspace Join URL ${isEditMode ? "(Optional)" : "*"}`}
                id="slack-workspace-url"
                name="slack_workspace_url"
                value={slackWorkspaceUrl}
                onChange={(e) => {
                  setSlackWorkspaceUrl(e.target.value);
                  setValidationErrors({
                    ...validationErrors,
                    slackWorkspaceUrl: undefined,
                  });
                }}
                placeholder="https://join.slack.com/t/yourworkspace/shared_invite/..."
                disabled={isLoading}
                error={validationErrors.slackWorkspaceUrl}
              />
              <p className="text-text-secondary text-xs mt-1">
                Use only: <strong>https://join.slack.com/t/&lt;workspace&gt;/shared_invite/...</strong>
              </p>
            </>
          )}

          <TextArea
            label="Description (Optional)"
            id="channel-description"
            name="description"
            value={description || ""}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            disabled={isLoading}
            maxLength={500}
          />
        </div>
      </div>

      {/* 3. Reminder Overrides */}
      <div className="space-y-6 mt-8">
        <div className="pb-4 border-b border-dark-tertiary/30">
          <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
            <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
              <span className="text-golden-accent font-bold text-sm">
                {reminderStepNumber}
              </span>
            </div>
            Reminder Overrides (Optional)
          </h2>
          <p className="text-text-secondary text-sm ml-11">Control reminder schedule for this channel</p>
        </div>

        <div className="ml-11 max-w-md">
          <Input
            label="Reminder Days Override"
            id="reminder-days-override"
            name="reminder_days_override"
            type="number"
            value={reminderDaysOverride}
            onChange={(e) => {
              setReminderDaysOverride(e.target.value);
              setValidationErrors({
                ...validationErrors,
                reminderDaysOverride: undefined,
              });
            }}
            placeholder="e.g., 3 (days before expiry) Default 2"
            min={0}
            disabled={isLoading}
            error={validationErrors.reminderDaysOverride}
          />
        </div>
      </div>

      <div className="space-y-6 mt-8">
        <div className="pb-4 border-b border-dark-tertiary/30">
          <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
            <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
              <span className="text-golden-accent font-bold text-sm">{couponStepNumber}</span>
            </div>
            Channel Coupon (Optional)
          </h2>
          <p className="text-text-secondary text-sm ml-11">
            Configure a channel-specific coupon. Universal coupons are managed separately and continue to work.
          </p>
        </div>

        <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Coupon Code"
            id="coupon-code"
            name="couponCode"
            value={couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value.toUpperCase());
              setValidationErrors({
                ...validationErrors,
                couponCode: undefined,
              });
            }}
            placeholder="6+ alphanumeric code"
            disabled={isLoading}
            error={validationErrors.couponCode}
            maxLength={32}
          />

          <Input
            label="Coupon Discount (%)"
            type="number"
            id="coupon-discount"
            name="couponDiscount"
            min={0}
            max={100}
            value={couponDiscount}
            onChange={(e) => {
              setCouponDiscount(e.target.value);
              setValidationErrors({
                ...validationErrors,
                couponDiscount: undefined,
              });
            }}
            placeholder="0-100"
            disabled={isLoading}
            error={validationErrors.couponDiscount}
          />
        </div>
      </div>

      {/* 4. Status (Edit only) */}
      {isEditMode && (
        <div className="space-y-4 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">
                    {statusStepNumber}
                </span>
              </div>
              Status
            </h2>
            <p className="text-text-secondary text-sm ml-11">Control active and KYC post-payment settings</p>
          </div>

          <div className="ml-11 space-y-4">
            <label
              htmlFor="is-active"
              className="text-sm font-medium text-text-secondary flex items-center cursor-pointer"
            >
              <input
                type="checkbox"
                id="is-active"
                className="form-checkbox h-4 w-4 text-golden-accent rounded mr-2 focus:ring-golden-focus-ring bg-dark-tertiary border-dark-border checked:bg-golden-accent checked:border-transparent focus:ring-offset-dark-primary"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isLoading}
              />
              Channel is Active
            </label>
            <p className="text-xs text-text-secondary">
              Inactive channels cannot be subscribed to publicly via referral links.
            </p>

            <label
              htmlFor="is-post"
              className="text-sm font-medium text-text-secondary flex items-center cursor-pointer"
            >
              <input
                type="checkbox"
                id="is-post"
                className="form-checkbox h-4 w-4 text-golden-accent rounded mr-2 focus:ring-golden-focus-ring bg-dark-tertiary border-dark-border checked:bg-golden-accent checked:border-transparent focus:ring-offset-dark-primary"
                checked={ispost}
                onChange={(e) => setIspost(e.target.checked)}
                disabled={isLoading}
              />
              Post Payment Kyc
            </label>
            <p className="text-xs text-text-secondary">
              Post Payment flow for kyc verification.
            </p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="mt-8 flex gap-2 justify-center">
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isLoading || (isSlack && isSlackSimulated)}
          variant="primary"
        >
          {isEditMode ? "Update Channel" : "Create Channel"}
        </Button>
      </div>
        </>
      )}
    </form>
  );
};

export default ChannelForm;
